import React, { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { theme } from '../../../theme/theme'
import { Poll } from '../Poll'
import { pollQueries, unvote, vote } from '../../../api/polls'
import { Button, ButtonVariant } from '../../common/Button'
import { PostPollCard, PostPollOption } from '../../../generated/api/post-row'
import { refreshPostRow, rowWithPoll, updatePostRow } from '../postRowCache'
import { useToastNotifications } from '../../../hooks/useToastNotification'
import { ToastType } from '../../common/Toast/toast-type'

interface PostPollProps {
  postId: string
  poll: PostPollCard
  disabled?: boolean
}

export const PostPoll = ({
  postId,
  poll,
  disabled,
}: PostPollProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const [selectedValue, setSelectedValue] = useState<string | null>(null)
  const [showResults, setShowResults] = useState(false)
  const queryClient = useQueryClient()
  const { setToastNotification } = useToastNotifications()

  const voted = poll.type === 'per-user' ? poll.canUndo : poll.householdVoted

  const applyCard = (card: PostPollCard): void => {
    queryClient.setQueryData(pollQueries.card(card.id).queryKey, card)
    updatePostRow(queryClient, postId, (row) => rowWithPoll(row, card))
  }

  const onFailure = (): void => {
    setToastNotification(
      ToastType.DANGER,
      translate('flashMessage.somethingWentWrongError')
    )
    refreshPostRow(queryClient, postId)
  }

  const { mutate: mutateUnVote, isPending: isRemovingVote } = useMutation({
    mutationFn: unvote,
    meta: { skipGlobalInvalidate: true },
    onSuccess: (response) => {
      setSelectedValue(null)
      setShowResults(false)
      applyCard(response.data.data)
    },
    onError: onFailure,
  })

  const { mutate: mutateVote, isPending: isVoting } = useMutation({
    mutationFn: vote,
    meta: { skipGlobalInvalidate: true },
    onSuccess: (response) => applyCard(response.data.data),
    onError: () => {
      setSelectedValue(null)
      onFailure()
    },
  })

  const onVote = (option: PostPollOption): void => {
    mutateVote({ optionId: option.id, id: poll.id })
  }

  const getVotesString = (): string => {
    return poll.totalVotes === 1
      ? `1 ${translate('poll.votesSingular')}`
      : `${poll.totalVotes} ${translate('poll.votesPlural')}`
  }

  const onRegretVote = (): void => {
    !disabled && mutateUnVote(poll.id)
  }

  const onBackToVote = (): void => {
    !disabled && setShowResults(false)
  }

  const onShowResults = (): void => {
    !disabled && setShowResults(true)
  }

  const renderRegretVote = poll.canUndo
  const renderBackToVote = !voted && showResults
  const renderShowResults = !voted || showResults
  const voteInProgress = isVoting || isRemovingVote

  return (
    <div className="flex flex-col gap-4 rounded-md border border-neutral-40 p-6">
      <div className="flex flex-col gap-2">
        <div className="flex items-center">
          <p style={theme.textVariants.base}>
            {poll.anonymous
              ? translate('poll.anonymousVoting')
              : translate('poll.nonAnonymousVoting')}
          </p>
          <p style={theme.textVariants.base} className="ml-auto text-right">
            ({getVotesString()})
          </p>
        </div>
        <p style={theme.textVariants.lead}>{poll.title}</p>
      </div>
      <div className="flex flex-col gap-3">
        {poll.options.map((option, i) => {
          return (
            <Poll
              key={option.id}
              option={option}
              totalVotes={poll.totalVotes}
              index={i}
              showResult={voted || showResults}
              selectedValue={selectedValue}
              setSelectedValue={setSelectedValue}
              vote={onVote}
              loading={voteInProgress}
              disabled={disabled || !poll.canVote}
            />
          )
        })}
      </div>
      <div className="flex justify-between">
        {voted && (
          <p style={theme.textVariants.base}>
            {translate(
              poll.type === 'per-user'
                ? 'poll.userAlreadyVoted'
                : 'poll.householdAlreadyVoted'
            )}
          </p>
        )}
        {renderBackToVote && (
          <button onClick={onBackToVote} disabled={disabled}>
            {translate('poll.vote')}
          </button>
        )}
        {renderShowResults && !renderBackToVote && (
          <button onClick={onShowResults} disabled={disabled}>
            <p style={theme.textVariants.base}>
              {translate('poll.showResults')}
            </p>
          </button>
        )}
        {renderRegretVote && (
          <Button
            variant={ButtonVariant.DEFAULT}
            onClick={onRegretVote}
            loading={voteInProgress}
            disabled={disabled}
            label={translate('poll.regretVote')}
          />
        )}
      </div>
    </div>
  )
}
